import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  comRepeticao, DEFAULT_RULES, ehColetiva, formatPoints, janelaDoPrazo, missaoDoModelo,
  modelosDisponiveis, pointsLabel, prazoDaMissao, PRAZOS_DE_MISSAO, resolveConfig,
  interacaoDe, type ChallengeEvent, type ModeloDeMissao,
} from "@nhomi/core";
import { useSession } from "@/lib/auth";
import { adicionarMissao, watchChallenge, type Challenge } from "@/data/challenges";
import { dayKey } from "@/data/plates";
import { Empty, Spinner, TopBar } from "@/ui/primitives";
import { PedidoDaMissao } from "@/ui/Missoes";
import { EditorDeMissao, novoId, type Rascunho } from "./Rules";

/* ============================================================
   Nova missão, direto da tela de Missões

   O caminho antigo era Detalhes → Regras → aba Missões → Criar, com um
   editor de 8 tipos e umas 20 condições aberto de cara. Aqui são três
   passos: escolher um modelo (ou do zero), ajustar só o que importa
   (quantidade, prêmio, prazo, repetir) e ver como fica antes de criar.
   As condições continuam lá, em "Personalizar", fechado.

   Salva SÓ a missão (`adicionarMissao`, numa transação) — sem regravar
   regras, refeições e selos, que é o que a tela de Regras faz.
   ============================================================ */

type Passo = "escolher" | "ajustar" | "ver";

export function NovaMissao() {
  const { cid = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const [challenge, setChallenge] = useState<Challenge | null | undefined>(undefined);
  const [passo, setPasso] = useState<Passo>("escolher");
  const [ev, setEv] = useState<ChallengeEvent | null>(null);
  const [prazo, setPrazo] = useState<number | "data">(7);
  const [repetir, setRepetir] = useState(false);
  const [personalizar, setPersonalizar] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => watchChallenge(cid, setChallenge), [cid]);
  const config = useMemo(() => resolveConfig(challenge), [challenge]);
  const hoje = dayKey();

  if (challenge === undefined) return <div className="screen center"><Spinner /></div>;
  if (!challenge) return <main className="screen"><Empty emoji="🤔" title="Desafio não encontrado" /></main>;
  if (challenge.ownerUid !== user?.uid) {
    return (
      <main className="screen">
        <TopBar title="Nova missão" back={`/c/${cid}/missoes`} />
        <Empty emoji="🔒" title="Só o dono do desafio cria missões" />
      </main>
    );
  }

  const ids = config.events.map((e) => e.id);
  const rascunho: Rascunho = {
    rules: { ...DEFAULT_RULES, ...config.rules }, meals: config.meals, cuisines: config.cuisines,
    events: ev ? [ev] : [],
  };
  const comPrazo = (e: ChallengeEvent, p: number | "data", rep: boolean): ChallengeEvent =>
    rep ? comRepeticao(e, hoje, true)
      : p === "data" ? { ...comRepeticao(e, hoje, false), from: hoje, to: e.to && e.to >= hoje ? e.to : janelaDoPrazo(hoje, 7).to }
        : { ...comRepeticao(e, hoje, false), ...janelaDoPrazo(hoje, p) };

  function escolher(m: ModeloDeMissao | null) {
    const base = m
      ? missaoDoModelo(m, novoId(m.id, ids), hoje)
      : { id: novoId("missao", ids), name: "Nova missão", emoji: "🎯", tipo: "contagem" as const,
        from: hoje, to: "", need: 5, bonus: 2, cuisineIds: [], condicoes: [] };
    const dias = m?.dias ?? 7;
    const p = PRAZOS_DE_MISSAO.some((x) => x.dias === dias) ? dias : "data";
    setPrazo(p);
    setRepetir(false);
    setEv(p === "data" ? base : { ...base, ...janelaDoPrazo(hoje, dias) });
    setPersonalizar(!m);
    setPasso("ajustar");
  }

  async function criar() {
    if (!ev) return;
    setSalvando(true);
    setErro("");
    try {
      await adicionarMissao(cid, ev);
      navigate(`/c/${cid}/missoes`);
    } catch {
      setSalvando(false);
      setErro("Não deu pra criar agora. Tenta de novo.");
    }
  }

  /* ---------- 1. escolher ---------- */
  if (passo === "escolher" || !ev) {
    const modelos = modelosDisponiveis(config, interacaoDe(challenge));
    return (
      <main className="screen">
        <TopBar title="Nova missão" back={`/c/${cid}/missoes`} />
        <div className="screen-body no-tabbar">
          <div className="passos"><span className="on">1 Escolher</span><span>2 Ajustar</span><span>3 Criar</span></div>
          <div className="card lista-crua">
            {[...new Set(modelos.map((m) => m.grupo))].map((grupo) => (
              <div key={grupo}>
                <div className="modelo-grupo">{grupo}</div>
                {modelos.filter((m) => m.grupo === grupo).map((m) => (
                  <button key={m.id} className="list-row" onClick={() => escolher(m)}>
                    <span className="ico emoji">{m.emoji}</span>
                    <span className="label">{m.name}<span className="sub">{m.resumo} · +{formatPoints(m.bonus)} pts</span></span>
                  </button>
                ))}
              </div>
            ))}
            <button className="list-row" onClick={() => escolher(null)}>
              <span className="ico emoji">✏️</span>
              <span className="label">Do zero<span className="sub">você escolhe o que conta</span></span>
            </button>
          </div>
          <div className="gap" />
        </div>
      </main>
    );
  }

  const mexer = (f: (e: ChallengeEvent) => void) => setEv((e) => {
    if (!e) return e;
    const c = structuredClone(e);
    f(c);
    return c;
  });
  const passoBonus = 0.5;

  /* ---------- 3. ver e criar ---------- */
  if (passo === "ver") {
    return (
      <main className="screen">
        <TopBar title="Nova missão" back={`/c/${cid}/missoes`} />
        <div className="screen-body no-tabbar">
          <div className="passos"><span>1 Escolher</span><span>2 Ajustar</span><span className="on">3 Criar</span></div>
          <div className="section-label left">Assim o grupo vai ver</div>
          <div className="card missao-card previa">
            <div className="missao-cabeca">
              <span className="missao-emoji">{ev.emoji || "🎯"}</span>
              <div className="missao-titulo">
                <div className="missao-nome">{ev.name}</div>
                <div className="missao-pede"><PedidoDaMissao e={ev} config={config} /></div>
              </div>
              <span className="missao-premio">+{pointsLabel(ev.bonus)}</span>
            </div>
            <div className="missao-eu-sub">
              {ehColetiva(ev) ? "O grupo" : "Você"}: 0/{ev.need} · {prazoDaMissao(ev, hoje)}
              {ev.repetir && " · renova toda segunda"}
            </div>
          </div>
          {erro && <div className="hint-row erro">{erro}</div>}
          <button className="btn btn-primary" disabled={salvando} onClick={() => void criar()}>
            {salvando ? "Criando…" : "Criar missão"}
          </button>
          <button className="btn btn-ghost" onClick={() => setPasso("ajustar")}>Voltar e ajustar</button>
          <div className="gap" />
        </div>
      </main>
    );
  }

  /* ---------- 2. ajustar ---------- */
  const valido = ev.name.trim().length > 0 && ev.need >= 1 && ev.bonus >= 0 && (!ev.to || ev.to >= ev.from);
  return (
    <main className="screen">
      <TopBar title="Nova missão" back={`/c/${cid}/missoes`} />
      <div className="screen-body no-tabbar">
        <div className="passos"><span>1 Escolher</span><span className="on">2 Ajustar</span><span>3 Criar</span></div>

        <div className="card ajuste">
          <div className="ajuste-topo">
            <span className="missao-emoji">{ev.emoji || "🎯"}</span>
            <div>
              <div className="missao-nome">{ev.name}</div>
              <div className="missao-pede"><PedidoDaMissao e={ev} config={config} /></div>
            </div>
          </div>

          <Contador rotulo="Quantidade" valor={ev.need} passo={1} minimo={1}
            texto={String(ev.need)} aoMudar={(v) => mexer((x) => { x.need = v; })} />
          <Contador rotulo="Prêmio" valor={ev.bonus} passo={passoBonus} minimo={0}
            texto={`+${pointsLabel(ev.bonus)}`} aoMudar={(v) => mexer((x) => { x.bonus = v; })} />

          <div className="ajuste-rotulo">Prazo</div>
          {repetir ? (
            <div className="hint-row">Até domingo, e renova sozinha toda segunda.</div>
          ) : (
            <div className="chip-wrap">
              {PRAZOS_DE_MISSAO.map((p) => (
                <button key={p.id} className={`chip ${prazo === p.dias ? "active" : ""}`}
                  onClick={() => { setPrazo(p.dias); setEv(comPrazo(ev, p.dias, false)); }}>{p.rotulo}</button>
              ))}
              <button className={`chip ${prazo === "data" ? "active" : ""}`}
                onClick={() => { setPrazo("data"); setEv(comPrazo(ev, "data", false)); }}>Uma data</button>
            </div>
          )}
          {!repetir && prazo === "data" && (
            <label className="ajuste-data">Até
              <input type="date" min={hoje} value={ev.to || ""}
                onChange={(e) => mexer((x) => { x.to = e.target.value; })} />
            </label>
          )}

          <div className="switch-row">
            <div className="switch-corpo">
              <div className="switch-titulo">Repetir toda semana</div>
              <div className="switch-sub">Renova na segunda; a da semana passada fica no histórico.</div>
            </div>
            <button className={`switch ${repetir ? "on" : ""}`} role="switch" aria-checked={repetir}
              aria-label="Repetir toda semana"
              onClick={() => { const r = !repetir; setRepetir(r); setEv(comPrazo(ev, prazo, r)); }}>
              <span />
            </button>
          </div>
        </div>

        <button className="btn btn-ghost personalizar" aria-expanded={personalizar}
          onClick={() => setPersonalizar((v) => !v)}>
          {personalizar ? "▾" : "▸"} Personalizar (nome, tipo e o que conta)
        </button>
        {personalizar && (
          <EditorDeMissao ev={ev} draft={rascunho}
            mexer={(f) => mexer(f)} apagar={() => { setEv(null); setPasso("escolher"); }} />
        )}

        <button className="btn btn-primary" disabled={!valido} onClick={() => setPasso("ver")}>Ver como fica</button>
        <div className="gap" />
      </div>
    </main>
  );
}

function Contador(
  { rotulo, valor, passo, minimo, texto, aoMudar }: {
    rotulo: string; valor: number; passo: number; minimo: number; texto: string; aoMudar: (v: number) => void;
  },
) {
  return (
    <div className="contador">
      <span className="ajuste-rotulo">{rotulo}</span>
      <button className="contador-btn" aria-label={`Menos ${rotulo.toLowerCase()}`} disabled={valor - passo < minimo}
        onClick={() => aoMudar(Math.max(minimo, Math.round((valor - passo) * 100) / 100))}>−</button>
      <span className="contador-v">{texto}</span>
      <button className="contador-btn" aria-label={`Mais ${rotulo.toLowerCase()}`}
        onClick={() => aoMudar(Math.round((valor + passo) * 100) / 100)}>+</button>
    </div>
  );
}
